import moment from "moment";
import { v4 as uuidv4 } from "uuid";
import { ReactComponent as EditIcon } from "assets/img/svg/GreyEditIcon.svg";
import IconButtonWrapper from "views/pages/manage/common/form-fields-mui/IconButtonWrapper.jsx";

import React, {
  forwardRef,
  useState,
  useEffect,
  useImperativeHandle,
} from "react";
import { GridRowModes } from "@mui/x-data-grid";

import { academicGroup, phaseDropdown, newRow } from "./constants";

import { ReactComponent as ActiveControl } from "assets/img/svg/ActiveControl.svg";
import { ReactComponent as InactiveControl } from "assets/img/svg/InactiveControl.svg";

import ADataGrid from "../../common/data-grid/ADataGrid";
import { AGridColDef } from "../../common/data-grid/ADataGridColDef";
import AAutoComplete from "../../common/form-fields-mui/AAutoComplete";
import ActionsCell from "./ActionCell";
import NoRowOverlay from "../../masters/holidayMaster/NoRowOverlay";
import {
  editCellContainerStyle,
  selectStyles,
} from "../../edpOps/stockManagement/mastersAndMapping/onBoardingMaterialIdSetup/constant";
import CustomDatePicker from "../../common/commonComponents/CustomDatePicker";

import {
  gridStyles,
  gridComponents,
} from "../../edpOps/issuanceIDcard/printIdCardAndMapRfid/constant";
import {
  capitalize,
  isObjEmpty,
} from "../../common/utils/methods/commonMethods/utilityMethod";
import { failureToast } from "../../common/utils/methods/toasterFunctions/function";
import { saveOrDeleteMsg } from "../../edpOps/stockManagement/mastersAndMapping/sapProfitCostCentreMapping/constant";
import AChip from "../../common/chip/AChip";
import CustomTooltip from "../../common/customTooltip/CustomTooltip";

const TableContent = forwardRef(
  (
    {
      rows,
      rowCount,
      crctDropdown,
      loader,
      currentPage,
      onPageChange,
      createHandler,
      updateHandler,
    },
    ref
  ) => {
    const [rowData, setRowData] = useState(rows);
    const [rowForm, setRowForm] = useState({});
    const [rowModesModel, setRowModesModel] = useState({});

    useEffect(() => {
      setRowData(rows);
    }, [rows]);

    const rowFormHandler = (value, key) => {
      setRowForm((prev) => ({ ...prev, [key]: value }));
    };

    const addNewRow = async () => {
      if (!isObjEmpty(rowForm)) {
        failureToast(saveOrDeleteMsg);
      } else {
        let randomId = uuidv4();
        let emptyRow = {
          ...newRow(),
          isNew: true,
          id: randomId,
        };
        startEditing(randomId, emptyRow);
        setRowData((prev) => [emptyRow, ...prev]);
      }
    };

    const startEditing = (id, form) => {
      setRowModesModel((oldModel) => ({
        [id]: { mode: GridRowModes.Edit, fieldToFocus: "phaseName" },
      }));
      setRowForm(form);
    };

    const stopEditing = () => {
      const currForm = { ...rowForm };
      setRowModesModel((oldModel) => ({
        [currForm?.id]: { mode: GridRowModes.View, ignoreModifications: true },
      }));
      setRowForm({});
    };

    useImperativeHandle(ref, () => ({
      addNewRow: addNewRow,
      startEditing: startEditing,
      stopEditing: stopEditing,
    }));

    const handleEditClick = (params) => {
      if (!isObjEmpty(rowForm)) {
        failureToast(saveOrDeleteMsg);
      } else if (moment(params?.row?.toDate).isBefore(moment(), "day")) {
        failureToast("Cannot edit past data");
        return;
      } else {
        startEditing(params?.id, params?.row);
      }
    };

    const handleCancelClick = async (params) => {
      if (params?.row?.isNew) {
        setRowData(rows);
        setRowForm({});
      } else {
        stopEditing(params?.id);
      }
    };

    const handleSaveClick = async () => {
      const { academicGroup, phaseName, crct, fromDate, toDate, status } =
        rowForm;

      const modifiedPayload = {
        academicGroup: academicGroup.value,
        phaseName: phaseName.value,
        crct: crct.value,
        fromDate: moment(fromDate).format("YYYY-MM-DD"),
        toDate: moment(toDate).format("YYYY-MM-DD"),
        status: status,
      };

      if (rowForm?.isNew) {
        await createHandler(modifiedPayload);
      } else {
        await updateHandler(modifiedPayload, rowForm.id);
      }
    };

    const columns = [
      new AGridColDef("phaseName", "Phase")
        .setMinWidth(50)
        .setFlex(0.5)
        .editable(true)
        .setValueGetter((params) => params?.value?.label || "-")
        .renderEditCellComponent((params) => (
          <div style={editCellContainerStyle}>
            <AAutoComplete
              currentValue={rowForm?.phaseName}
              handleChange={(value) => rowFormHandler(value, "phaseName")}
              items={phaseDropdown}
              isMulti={false}
              style={selectStyles}
              autoFocus={params?.hasFocus}
            />
          </div>
        )),

      new AGridColDef("academicGroup", "Academic Group")
        .setMinWidth(150)
        .setFlex(0.5)
        .editable(true)
        .setValueGetter((params) => params?.value?.label || "-")
        .renderEditCellComponent((params) => (
          <div style={editCellContainerStyle}>
            <AAutoComplete
              currentValue={rowForm?.academicGroup}
              handleChange={(value) => rowFormHandler(value, "academicGroup")}
              items={academicGroup}
              isMulti={false}
              style={selectStyles}
              autoFocus={params?.hasFocus}
            />
          </div>
        )),

      new AGridColDef("crct", "CRCT")
        .setMinWidth(200)
        .setFlex(0.5)
        .editable(true)
        .setValueGetter((params) => params?.value?.label || "-")
        .renderEditCellComponent((params) => (
          <div style={editCellContainerStyle}>
            <AAutoComplete
              currentValue={rowForm?.crct}
              handleChange={(value) => rowFormHandler(value, "crct")}
              items={crctDropdown}
              isMulti={false}
              style={selectStyles}
              autoFocus={params?.hasFocus}
            />
          </div>
        )),

      new AGridColDef("fromDate", "Start Date")
        .setMinWidth(100)
        .setFlex(0.5)
        .editable(true)
        .renderCellComponent((params) => (
          <div>{moment(params?.value).format("DD-MMM-YYYY")}</div>
        ))
        .renderEditCellComponent((params) => (
          <div style={editCellContainerStyle}>
            <CustomDatePicker
              placeHolder="Select"
              minDate={moment(new Date())}
              value={rowForm?.fromDate ? moment(rowForm?.fromDate) : null}
              handler={(index, date, keyName) => {
                rowFormHandler(date, "fromDate");

                if (moment(rowForm?.toDate).isBefore(date, "day"))
                  rowFormHandler(date, "toDate");
              }}
            />
          </div>
        )),

      new AGridColDef("toDate", "End Date")
        .setMinWidth(100)
        .setFlex(0.5)
        .editable(true)
        .renderCellComponent((params) => (
          <div>{moment(params?.value).format("DD-MMM-YYYY")}</div>
        ))
        .renderEditCellComponent((params) => (
          <div style={editCellContainerStyle}>
            <CustomDatePicker
              placeHolder="Select"
              minDate={moment(rowForm.fromDate)}
              value={rowForm.toDate ? moment(rowForm?.toDate) : null}
              handler={(index, date, keyName) => {
                rowFormHandler(date, "toDate");
              }}
            />
          </div>
        )),

      new AGridColDef("updatedOn", "Updated Date Time")
        .setMinWidth(150)
        .setFlex(0.5)
        .editable(false)
        .renderCellComponent((params) =>
          params?.value ? (
            <div className="d-flex align-items-center">
              {moment(params?.value).format("YYYY-MM-DD HH:mm:ss")}
            </div>
          ) : (
            <div>-</div>
          )
        ),

      new AGridColDef("status", "Status")
        .setMinWidth(80)
        .columnAlign("center")
        .setFlex(0.5)
        .editable(true)
        .renderCellComponent((params) => (
          <AChip
            label={capitalize(params?.value)}
            success={params.value === "ACTIVE"}
          />
        ))
        .renderEditCellComponent((params) => (
          <div style={editCellContainerStyle}>
            <span>
              {rowForm.status === "ACTIVE" ? (
                <ActiveControl
                  onClick={() => rowFormHandler("INACTIVE", "status")}
                />
              ) : (
                <InactiveControl
                  onClick={() => rowFormHandler("ACTIVE", "status")}
                />
              )}
              <span className="ml-1">Active</span>
            </span>
          </div>
        )),

      new AGridColDef("actions", "Action", false)
        .setMinWidth(140)
        .setFlex(0.5)
        .renderCellComponent((params) =>
          moment(params?.row?.toDate).isBefore(moment(), "day") ? (
            <>
              <CustomTooltip title="You can’t edit this configuration because it is in the past">
                <div>
                  <IconButtonWrapper>
                    <EditIcon />
                  </IconButtonWrapper>
                </div>
              </CustomTooltip>
            </>
          ) : (
            <ActionsCell
              params={params}
              handleEditClick={handleEditClick}
              handleCancelClick={handleCancelClick}
              handleSaveClick={handleSaveClick}
              rowForm={rowForm}
            />
          )
        ),
    ];

    return (
      <div className="mt-3">
        <ADataGrid
          removeWrapperContainer
          rowModesModel={rowModesModel}
          rows={rowData || []}
          columns={columns}
          rowId={(row) => row.id}
          rowHeight={50}
          loading={false}
          rowCount={rowCount}
          nextButtonHandler={onPageChange}
          currentPage={currentPage}
          components={{
            ...gridComponents,
            NoRowsOverlay: () => (
              <NoRowOverlay message={loader ? "Loading..." : null} />
            ),
          }}
          sx={gridStyles}
          autoHeight
          pinLastColumn
        />
      </div>
    );
  }
);

export default TableContent;
